import { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material';
import '@mui/x-date-pickers/themeAugmentation';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label1: true;
    label2: true;
    body3: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    dense: true;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      primary: string;
      primaryDark: string;
      primaryLight: string;
      success: string;
      textColor: string;
      iconColor: string;
      hoverColor: string;
      borderColor: string;
      inputBorder: string;
      tableBorder: string;
      tableHover: string;
      grayText: string;
      secondaryGrayText: string;
      grayBackground: string;
      darkGrayBackground: string;
      alert: string;
      redBackground: string;
      errorBorder: string;
      orange: string;
      orange2: string;
      tooltip: string;
      red: string;
      lightBlueColor: string;
      green: string;
      greenBackground: string;
      lightGreenBackground: string;
      lightBlueBackground: string;
      tabsBorderColor: string;
      greenBackgroundChip: string;
      blueBackgroundInfo: string;
      blueBackgroundQuestion: string;
    };
  }

  interface ThemeOptions {
    colors?: {
      primary?: string;
      primaryDark?: string;
      primaryLight?: string;
      success?: string;
      textColor?: string;
      iconColor?: string;
      hoverColor?: string;
      borderColor?: string;
      inputBorder?: string;
      tableBorder?: string;
      tableHover?: string;
      grayText?: string;
      secondaryGrayText?: string;
      grayBackground?: string;
      darkGrayBackground?: string;
      alert?: string;
      redBackground?: string;
      errorBorder?: string;
      orange?: string;
      orange2?: string;
      tooltip?: string;
      red?: string;
      lightBlueColor?: string;
      green?: string;
      greenBackground?: string;
      lightGreenBackground?: string;
      lightBlueBackground?: string;
      greenBackgroundChip?: string;
      blueBackgroundInfo?: string;
      blueBackgroundQuestion?: string;
      tabsBorderColor?: string;
    };
  }
}

const options: ThemeOptions = {
  colors: {
    primary: '#6387e6',
    primaryLight: '#c9d8ff',
    primaryDark: '#455ea1',
    success: '#2bc268',
    textColor: '#1c2232',
    iconColor: '#8e9199',
    hoverColor: '#606470',
    borderColor: '#dededb',
    inputBorder: '#e2e1e6',
    tableBorder: '#e6e6e3',
    tableHover: '#f8f7fc',
    grayText: '#8e9199',
    secondaryGrayText: '#8e9096',
    grayBackground: '#f3f2f7',
    darkGrayBackground: '#aaa9ac',
    alert: '#f7ebcd',
    redBackground: '#fceff0',
    errorBorder: '#ff7b7b',
    orange: '#eb8b46',
    orange2: '#ea875d',
    tooltip: '#fff5d1',
    red: '#de606c',
    lightBlueColor: '#abc2ff',
    green: '#009f53',
    greenBackground: '#57a35b',
    lightGreenBackground: '#e5fbda',
    lightBlueBackground: '#eaeefc',
    tabsBorderColor: '#e2e1e5',
    greenBackgroundChip: 'rgba(0, 159, 83, 0.4)',
    blueBackgroundInfo: '#abbdef',
    blueBackgroundQuestion: '#2f80ed',
  },
};

const isPrerender: boolean = navigator.userAgent === 'ReactSnap' || navigator.userAgent === 'Prerender';

const theme: Theme = createTheme(options);

export default createTheme({
  ...theme,
  breakpoints: {
    values: {
      xs: 0,
      sm: isPrerender ? 0 : 600,
      md: isPrerender ? 0 : 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: theme.colors.primary,
    },
    secondary: {
      main: theme.colors.grayBackground,
    },
    text: {
      primary: theme.colors.textColor,
    },
    error: {
      main: theme.colors.errorBorder,
    },
    success: {
      main: theme.colors.success,
    },
  },
  typography: {
    fontFamily: ['Rubik', 'sans-serif'].join(', '),
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '24px',
          lineHeight: '32px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '20px',
            lineHeight: '24px',
          },
        },
        h2: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '32px',
        },
        h4: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '24px',
        },
        h5: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '20px',
        },
        h6: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
        },
        body1: {
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '16px',
          lineHeight: '24px',
        },
        body2: {
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '14px',
          lineHeight: '20px',
        },
        caption: {
          fontStyle: 'normal',
          fontSize: '12px',
          fontWeight: 'normal',
          lineHeight: '16px',
          color: theme.colors.grayText,
        },
      },
      variants: [
        {
          props: { variant: 'body3' },
          style: {
            fontStyle: 'normal',
            fontWeight: 'normal',
            fontSize: '12px',
            lineHeight: '16px',
          },
        },
        {
          props: { variant: 'label1' },
          style: {
            fontSize: '12px',
            lineHeight: '16px',
            fontStyle: 'normal',
            fontWeight: 'normal',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          },
        },
        {
          props: { variant: 'label2' },
          style: {
            fontFamily: 'Rubik',
            fontSize: '10px',
            lineHeight: '16px',
            fontStyle: 'normal',
            fontWeight: 'normal',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '6px',
          paddingTop: '12px',
          paddingBottom: '12px',
          textTransform: 'none',
          fontSize: '16px',
          lineHeight: '24px',
        },
        outlined: {
          paddingTop: '11px',
          paddingBottom: '11px',
        },
        sizeSmall: {
          fontSize: '14px',
          lineHeight: '20px',
          paddingTop: '10px',
          paddingBottom: '10px',
        },
        outlinedSizeSmall: {
          paddingTop: '9px',
          paddingBottom: '9px',
        },
        containedPrimary: {
          ':disabled': {
            background: theme.colors.primary,
            opacity: 0.5,
            color: '#ffffff',
          },
        },
        textPrimary: {
          ':hover': {
            color: theme.colors.primaryDark,
            background: 'transparent',
          },
        },
      },
      variants: [
        {
          props: { color: 'error' },
          style: {
            color: theme.colors.red,
            borderColor: theme.colors.red,
          },
        },
      ],
    },
    MuiIconButton: {
      variants: [
        {
          props: { size: 'dense' },
          style: {
            padding: '8px',
            margin: '-8px',
            '&:hover': {
              background: 'none',
            },
          },
        },
      ],
      styleOverrides: {
        colorPrimary: {
          color: theme.colors.primary,
          '&:hover': {
            color: theme.colors.primaryDark,
          },
        },
        root: {
          color: theme.colors.iconColor,
          '&:hover': {
            color: theme.colors.hoverColor,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          minWidth: theme.spacing(40),
          boxShadow: '2px 0px 44px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.colors.borderColor,
          '&::before': {
            borderColor: theme.colors.borderColor,
          },
          '&::after': {
            borderColor: theme.colors.borderColor,
          },
        },
        wrapper: {
          color: theme.colors.grayText,
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
        },
        textAlignLeft: {
          '&::before': {
            width: 0,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: theme.spacing(1),
          backgroundColor: theme.colors.tooltip,
          color: theme.colors.textColor,
          borderRadius: '6px',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
          fontSize: '12px',
          lineHeight: '16px',
          fontWeight: 'normal',
          fontStyle: 'normal',
          maxWidth: '268px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: theme.colors.grayText,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '24px',
          borderRadius: '6px',
          backgroundColor: 'white',
        },
        input: {
          height: '24px',
          padding: '12px 8px',
        },
        sizeSmall: {
          fontSize: '14px',
          lineHeight: '20px',
        },
        inputSizeSmall: {
          height: '20px',
          padding: '10px 8px',
        },
        notchedOutline: {
          borderColor: theme.colors.inputBorder,
        },
        multiline: {
          padding: '0px',
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        sectionsContainer: {
          padding: '12.5px 0 !important',
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '24px',
          borderRadius: '6px',
          backgroundColor: 'white',
        },
        notchedOutline: {
          borderColor: theme.colors.inputBorder,
          // Prevent the legend from creating a notch (gap) in the border.
          // x-date-pickers v8 Outline always sets shrink=true on focus,
          // which opens a notch for the label. When InputLabelProps.shrink=false
          // the label stays inside the field, leaving a blank gap in the border.
          '& legend': {
            width: '0 !important',
            padding: '0 !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '20px',
          borderBottom: '1px solid ' + theme.colors.tableBorder,
        },
        head: {
          fontSize: '10px',
          lineHeight: '14px',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontWeight: 'normal',
          color: theme.colors.grayText,
          padding: theme.spacing(1, 2, 0.875),
        },
        body: {
          padding: theme.spacing(2, 2, 1.875),
          height: theme.spacing(7),
        },
        sizeSmall: {
          padding: theme.spacing(1.5, 1, 1.375),
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: theme.colors.tableHover,
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '24px',
          borderRadius: '30px',
        },
        icon: {
          width: '14px',
          height: '14px',
          marginRight: '-4px',
        },
        label: {
          fontSize: '12px',
          lineHeight: '14px',
          paddingLeft: '9px',
          paddingRight: '9px',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&.Mui-active .MuiTableSortLabel-icon': {
            color: '#3b8cdf',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingTop: theme.spacing(5),
          paddingBottom: theme.spacing(4),
          [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(2.5),
            paddingBottom: theme.spacing(3),
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.colors.grayText,
          fontSize: theme.spacing(1.25),
          lineHeight: theme.spacing(2),
          margin: '3px 0 0 0',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(0, -22px)',
          textTransform: 'uppercase',
          color: theme.colors.grayText,
          fontSize: theme.spacing(1.5),
          lineHeight: theme.spacing(2),
          letterSpacing: '0.05em',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          position: 'absolute',
          whiteSpace: 'nowrap',
          transform: 'translate(0, -22px)',
          textTransform: 'uppercase',
          color: theme.colors.grayText,
          fontSize: theme.spacing(1.5),
          lineHeight: theme.spacing(2),
          letterSpacing: '0.05em',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-root': {
            padding: '0px',
            '.MuiAutocomplete-input': {
              padding: '12px 8px',
            },
            '.MuiAutocomplete-clearIndicator': {
              visibility: 'visible',
              marginRight: '2px',
            },
            '.MuiAutocomplete-endAdornment': {
              right: 0,
              top: 'inherit',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          minWidth: '500px',
        },
        paperFullScreen: {
          borderRadius: 'unset',
          minWidth: '0',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3, 3, 4),
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3, 3, 3),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0, 3, 3),
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: theme.colors.grayText,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: theme.colors.grayText,
        },
      },
    },
  },
});
